import { useEffect, useState } from "react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";
import type { Supplier, SupplierFormData } from "~/types/purchasing";

interface SupplierDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  supplier?: Supplier;
  onSubmit: (data: SupplierFormData) => void;
  isLoading: boolean;
}

const emptyForm: SupplierFormData = {
  name: "",
  rut: "",
  giro: "",
  address: "",
  comuna: "",
  phone: "",
  email: "",
  contact_name: "",
  notes: "",
  is_active: true,
  razon_social: "",
  payment_condition: "",
  bank_name: "",
  bank_account: "",
};

export function SupplierDialog({
  open,
  onOpenChange,
  supplier,
  onSubmit,
  isLoading,
}: SupplierDialogProps) {
  const [formData, setFormData] = useState<SupplierFormData>(emptyForm);

  useEffect(() => {
    if (supplier) {
      setFormData({
        name: supplier.name,
        rut: supplier.rut || "",
        giro: supplier.giro || "",
        address: supplier.address || "",
        comuna: supplier.comuna || "",
        phone: supplier.phone || "",
        email: supplier.email || "",
        contact_name: supplier.contact_name || "",
        notes: supplier.notes || "",
        is_active: supplier.is_active,
        razon_social: supplier.razon_social || "",
        payment_condition: supplier.payment_condition || "",
        bank_name: supplier.bank_name || "",
        bank_account: supplier.bank_account || "",
      });
    } else {
      setFormData(emptyForm);
    }
  }, [supplier, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const isEditing = !!supplier;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Editar Proveedor" : "Nuevo Proveedor"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="name">Nombre *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder="Nombre del proveedor"
              required
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="razon_social">Razón social</Label>
            <Input
              id="razon_social"
              value={formData.razon_social}
              onChange={(e) =>
                setFormData({ ...formData, razon_social: e.target.value })
              }
              placeholder="Razón social legal"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="rut">RUT</Label>
            <Input
              id="rut"
              value={formData.rut}
              onChange={(e) =>
                setFormData({ ...formData, rut: e.target.value })
              }
              placeholder="12.345.678-9"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="giro">Giro</Label>
            <Input
              id="giro"
              value={formData.giro}
              onChange={(e) =>
                setFormData({ ...formData, giro: e.target.value })
              }
              placeholder="Actividad económica"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="payment_condition">Condición de pago</Label>
            <Input
              id="payment_condition"
              value={formData.payment_condition}
              onChange={(e) =>
                setFormData({ ...formData, payment_condition: e.target.value })
              }
              placeholder="Ej: CREDITO 30 DIAS"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="bank_name">Banco</Label>
              <Input
                id="bank_name"
                value={formData.bank_name}
                onChange={(e) =>
                  setFormData({ ...formData, bank_name: e.target.value })
                }
                placeholder="Banco"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="bank_account">Cuenta bancaria</Label>
              <Input
                id="bank_account"
                value={formData.bank_account}
                onChange={(e) =>
                  setFormData({ ...formData, bank_account: e.target.value })
                }
                placeholder="N° de cuenta"
              />
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="contact_name">Nombre de Contacto</Label>
            <Input
              id="contact_name"
              value={formData.contact_name}
              onChange={(e) =>
                setFormData({ ...formData, contact_name: e.target.value })
              }
              placeholder="Persona de contacto"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                placeholder="email@ejemplo.com"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="phone">Teléfono</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                placeholder="+56 9 1234 5678"
              />
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="address">Dirección</Label>
            <Input
              id="address"
              value={formData.address}
              onChange={(e) =>
                setFormData({ ...formData, address: e.target.value })
              }
              placeholder="Dirección del proveedor"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="comuna">Comuna</Label>
            <Input
              id="comuna"
              value={formData.comuna}
              onChange={(e) =>
                setFormData({ ...formData, comuna: e.target.value })
              }
              placeholder="Comuna o ciudad"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="notes">Notas</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              placeholder="Notas adicionales..."
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading || !formData.name}>
              {isLoading ? "Guardando..." : isEditing ? "Guardar" : "Crear"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
